import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ColourBar.css";

const rangeStart = (range) => Number(range?.start ?? range?.min);
const rangeEnd = (range) => Number(range?.end ?? range?.max);
const formatValue = (value) => Number.isFinite(value) ? Number(value.toFixed(2)).toLocaleString() : "—";

export default function ColourBar({ scale, onBoundaryChange, onColorChange, onReset, label, className = "", boundaryStep = 1 }) {
  const barRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const ranges = useMemo(() => scale?.ranges || [], [scale]);
  const minimum = Number(scale?.min ?? rangeStart(ranges[0]));
  const maximum = Number(scale?.max ?? rangeEnd(ranges[ranges.length - 1]));
  const span = maximum - minimum;
  const pct = useCallback((value) => span > 0 ? ((value - minimum) / span) * 100 : 0, [minimum, span]);

  const onMove = useCallback((event) => {
    if (dragIdx === null || !barRef.current || !span || !onBoundaryChange) return;
    const rect = barRef.current.getBoundingClientRect();
    const position = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
    const value = minimum + (span * position) / rect.width;
    const lower = dragIdx === 0 ? minimum : rangeEnd(ranges[dragIdx - 1]);
    const upper = rangeEnd(ranges[dragIdx + 1]);
    const nextValue = boundaryStep > 0 ? Math.round(value / boundaryStep) * boundaryStep : value;
    if (nextValue > lower && nextValue < upper) onBoundaryChange(nextValue, dragIdx);
  }, [boundaryStep, dragIdx, minimum, onBoundaryChange, ranges, span]);

  useEffect(() => {
    if (dragIdx === null) return undefined;
    const stop = () => setDragIdx(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", stop);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", stop);
    };
  }, [dragIdx, onMove]);

  if (!scale || ranges.length < 2 || !Number.isFinite(minimum) || !Number.isFinite(maximum)) return null;

  return <div className={`colourBar ${className}`.trim()}>
    <div className="colourBar-heading">
      {label && <strong>{label}</strong>}
      {onReset && <button type="button" onClick={onReset}>Reset</button>}
    </div>
    <div ref={barRef} className="colourBar-track">
      {ranges.map((range, index) => <div key={index} className="colourBar-segment" style={{ width: `${Math.max(0, pct(rangeEnd(range)) - pct(rangeStart(range)))}%`, background: range.color }}>
        {onColorChange && <input type="color" value={range.color} aria-label={`Change range ${index + 1} color`} onChange={(event) => onColorChange(event.target.value, index)} />}
      </div>)}
      {!!onBoundaryChange && ranges.slice(0, -1).map((range, index) => <button key={index} type="button" className="colourBar-handle" aria-label={`Adjust boundary ${index + 1}`} style={{ left: `${pct(rangeEnd(range))}%` }} onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDragIdx(index);
      }} />)}
    </div>
    <div className="colourBar-ranges">
      {ranges.map((range, index) => <label key={index}><i style={{ background: range.color }} /><span>{formatValue(rangeStart(range))}–{formatValue(rangeEnd(range))}</span></label>)}
    </div>
  </div>;
}
